/**
 * Storefront quote-cart state, persisted to localStorage and shared across
 * pages via custom events. The Shopify cart and this quote cart are
 * intentionally separate — items added here never enter checkout; they're
 * batched into one staff-app quote when the user submits.
 */
(function () {
  const STORAGE_KEY = 'slc-quote-cart';
  const CHANGE_EVENT = 'quote-cart:change';
  const MAX_ITEMS = 50;
  const MAX_QTY = 10000;

  function read() {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter(isValidItem) : [];
    } catch {
      // Storage may be unavailable (private mode quotas, disabled APIs) or
      // hold corrupt JSON from an older schema — start fresh rather than
      // crashing every page that reads the quote cart.
      return [];
    }
  }

  function write(items) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      // Same rationale as read(): swallow so a full localStorage doesn't
      // turn an "Add to quote" click into a thrown exception.
    }
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { items } }));
  }

  function isValidItem(item) {
    return (
      item &&
      typeof item === 'object' &&
      typeof item.variant_id === 'string' &&
      item.variant_id.length > 0 &&
      Number.isFinite(item.quantity) &&
      item.quantity > 0
    );
  }

  function clampQuantity(value) {
    const n = Number.parseFloat(value);
    if (!Number.isFinite(n) || n <= 0) return 0;
    return Math.min(n, MAX_QTY);
  }

  const QuoteCart = {
    items() {
      return read();
    },

    count() {
      return read().reduce((sum, item) => sum + (item.quantity || 0), 0);
    },

    /**
     * Add an item. If the variant_id already exists, quantities are summed
     * — same behavior as Shopify's cart, which users will be familiar with.
     */
    add(item) {
      const quantity = clampQuantity(item.quantity);
      if (!quantity || !item.variant_id) return null;

      const items = read();
      const existing = items.find((i) => i.variant_id === item.variant_id);
      if (existing) {
        existing.quantity = clampQuantity(existing.quantity + quantity);
        // Refresh metadata in case the product/variant title was edited
        // since the user last added it.
        existing.product_title = item.product_title || existing.product_title;
        existing.variant_title = item.variant_title || existing.variant_title;
        existing.options = item.options || existing.options;
        existing.image_url = item.image_url || existing.image_url;
        existing.product_url = item.product_url || existing.product_url;
        existing.sku = item.sku || existing.sku;
        if (Number.isFinite(item.price_cents)) existing.price_cents = item.price_cents;
        write(items);
        return existing;
      }

      if (items.length >= MAX_ITEMS) {
        // Backend caps the request at MAX_ITEMS too. Bail loudly here so the
        // UI can show a "quote is full" message rather than silently dropping.
        return null;
      }

      const next = {
        variant_id: String(item.variant_id),
        sku: item.sku || null,
        product_title: item.product_title || '',
        variant_title: item.variant_title || '',
        options: Array.isArray(item.options) ? item.options : [],
        quantity,
        price_cents: Number.isFinite(item.price_cents) ? item.price_cents : null,
        image_url: item.image_url || null,
        product_url: item.product_url || null,
        // Admin-only: a per-line description override (catalog items) or the
        // typed description of a custom one-off line. `custom` lines carry a
        // synthetic variant_id minted by the caller, so they never merge with
        // a catalog variant above.
        description: item.description || null,
        custom: !!item.custom,
      };
      items.push(next);
      write(items);
      return next;
    },

    /**
     * Set/clear a per-line description override (admin "Edit" flow). Passing
     * an empty/blank string clears it so the line falls back to its variant
     * title. Silent no-op if the variant isn't in the cart.
     */
    setDescription(variantId, description) {
      const items = read();
      const item = items.find((i) => i.variant_id === String(variantId));
      if (!item) return;
      const next = (description || '').trim() || null;
      if (item.description === next) return;
      item.description = next;
      write(items);
    },

    /**
     * Patch the editable fields of a custom line in place (admin "Edit" flow
     * for custom products). Ignores undefined fields so callers can update a
     * subset. Silent no-op if the variant isn't a custom line in the cart.
     */
    updateCustom(variantId, patch) {
      const items = read();
      const item = items.find((i) => i.variant_id === String(variantId));
      if (!item || !item.custom) return;
      if (patch.name !== undefined) item.product_title = String(patch.name || '');
      if (patch.description !== undefined) {
        item.description = (patch.description || '').trim() || null;
      }
      if (patch.priceCents !== undefined && Number.isFinite(patch.priceCents)) {
        item.price_cents = patch.priceCents;
      }
      if (patch.quantity !== undefined) {
        item.quantity = clampQuantity(patch.quantity) || item.quantity;
      }
      write(items);
    },

    /**
     * Set/replace the cached `price_cents` for a single line. Used by the
     * quote page to backfill items that were added before price capture
     * shipped, or to refresh stale prices via /products/<handle>.js.
     * Silent no-op if the variant isn't in the cart.
     */
    setPrice(variantId, priceCents) {
      if (!Number.isFinite(priceCents)) return;
      const items = read();
      const item = items.find((i) => i.variant_id === String(variantId));
      if (!item) return;
      if (item.price_cents === priceCents) return;
      item.price_cents = priceCents;
      write(items);
    },

    remove(variantId) {
      const items = read().filter((i) => i.variant_id !== String(variantId));
      write(items);
    },

    setQuantity(variantId, quantity) {
      const qty = clampQuantity(quantity);
      const items = read();
      const item = items.find((i) => i.variant_id === String(variantId));
      if (!item) return;
      if (qty <= 0) {
        this.remove(variantId);
        return;
      }
      item.quantity = qty;
      write(items);
    },

    clear() {
      write([]);
    },

    onChange(handler) {
      window.addEventListener(CHANGE_EVENT, handler);
      return () => window.removeEventListener(CHANGE_EVENT, handler);
    },

    STORAGE_KEY,
    CHANGE_EVENT,
    MAX_ITEMS,
  };

  window.QuoteCart = QuoteCart;

  // Fire an initial change event so listeners that were registered before
  // QuoteCart loaded (e.g., the header icon-bubble) hydrate to the current
  // localStorage state on page load. Wrapping in queueMicrotask defers it
  // until the current script finishes assigning to window.QuoteCart, so the
  // listeners' update() calls see the helper.
  if (typeof queueMicrotask === 'function') {
    queueMicrotask(() => {
      window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { items: read() } }));
    });
  } else {
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { items: read() } }));
    }, 0);
  }
})();
